import type { JSX } from 'react';
import './BootLoader.css';

/**
 * Cold-boot loading surface. Rendered by App.tsx's BootFallback while the
 * session resolves identity → connect → join. A quiet pre-curtain moment:
 * warm-paper cream, single-line wordmark, a drawn-arc spinner. Renders
 * outside the PageFrame layout wrapper — this threshold precedes the book.
 *
 * Handoff: when the session is ready, BootFallback unmounts and <Lobby>
 * mounts behind its own entry fade. Both surfaces share --susdle-paper,
 * so the handoff reads as continuous paper with content swapping on top.
 */
export function BootLoader(): JSX.Element {
  return (
    <main className="susdle-boot-loader" aria-label="Loading Susdle">
      <div className="susdle-boot-loader__inner">
        <h1 className="susdle-boot-loader__wordmark susdle-display">SUSDLE</h1>
        <BootSpinner />
      </div>
    </main>
  );
}

/* Drawn-arc spinner: the stroke draws clockwise to ~78% circumference,
   holds briefly, fades, restarts. Same currentColor + stroke weight as
   the wordmark stroke so the two read as the same hand. Butt linecap for
   the woodcut squareness called out in the brief. */
function BootSpinner(): JSX.Element {
  return (
    <svg
      className="susdle-boot-loader__spinner"
      viewBox="0 0 40 40"
      aria-hidden
      focusable={false}
    >
      <circle
        className="susdle-boot-loader__spinner-arc"
        cx="20"
        cy="20"
        r="14"
        pathLength={100}
      />
    </svg>
  );
}
